import { Dialog } from '@/ui/primitives/Dialog';
import { cn } from '@/lib/utils/cn';
import type { RecurrenceEditScope } from '@/domain/types';

const OPTIONS: { value: RecurrenceEditScope; label: string; hint: string }[] = [
  { value: 'dieser', label: 'Nur dieser Termin', hint: 'Die übrigen Termine der Serie bleiben unverändert.' },
  { value: 'dieser_und_folgende', label: 'Dieser und alle folgenden', hint: 'Frühere Termine bleiben, wie sie sind.' },
  { value: 'alle', label: 'Alle Termine', hint: 'Einzeln angepasste Termine behalten ihre Änderung.' },
];

/** Dieselbe Rueckfrage fuer wiederkehrende Aufgaben (F-24). */
const AUFGABE: { value: RecurrenceEditScope; label: string; hint: string }[] = [
  { value: 'dieser', label: 'Nur dieses Mal', hint: 'Gilt nur für das aktuelle Vorkommen, die Serie läuft danach normal weiter.' },
  { value: 'dieser_und_folgende', label: 'Dieses und alle folgenden', hint: 'Bereits Erledigtes bleibt, wie es ist.' },
  { value: 'alle', label: 'Alle', hint: 'Auch bereits erledigte Vorkommen der Serie.' },
];

interface ScopeDialogProps {
  open: boolean;
  mode: 'bearbeiten' | 'loeschen';
  onPick: (scope: RecurrenceEditScope) => void;
  onCancel: () => void;
  /** Die Wiederholung selbst aendert sich - das geht nicht fuer einen einzelnen Termin. */
  ohneEinzeln?: boolean;
  /** Wiederkehrende Aufgabe statt Serientermin (F-24). */
  art?: 'termin' | 'aufgabe';
}

/**
 * Fragt, welche Vorkommen einer Serie betroffen sind.
 *
 * Der Hinweis zu "Alle Termine" ist wichtig: dass einzeln verschobene Termine
 * ihre Aenderung behalten, ueberrascht sonst - es ist aber das Verhalten, das
 * man von Google Kalender kennt.
 */
export function ScopeDialog({ open, mode, onPick, onCancel, ohneEinzeln = false, art = 'termin' }: ScopeDialogProps) {
  const aufgabe = art === 'aufgabe';
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={aufgabe
        ? (mode === 'loeschen' ? 'Wiederkehrende Aufgabe löschen' : 'Wiederkehrende Aufgabe ändern')
        : (mode === 'loeschen' ? 'Serientermin löschen' : 'Serientermin ändern')}
      description={aufgabe ? 'Wiederkehrende Aufgabe — was soll gelten?' : 'Wiederkehrender Termin — was soll geändert werden?'}
      footer={null}
    >
      <div className="flex flex-col gap-1.5">
        {(aufgabe ? AUFGABE : OPTIONS).filter((o) => !(ohneEinzeln && o.value === 'dieser')).map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => onPick(option.value)}
            className={cn(
              'rounded-lg border border-line px-3.5 py-2.5 text-left transition-colors',
              'hover:border-line-strong hover:bg-surface-raised',
            )}
          >
            <span className="block text-sm font-medium text-ink">{option.label}</span>
            <span className="mt-0.5 block text-[12px] leading-snug text-ink-muted">
              {option.hint}
            </span>
          </button>
        ))}

        <button
          type="button"
          onClick={onCancel}
          className="mt-2 h-9 rounded-md text-sm text-ink-muted hover:text-ink"
        >
          Abbrechen
        </button>
      </div>
    </Dialog>
  );
}
